import { datadogRum } from "@datadog/browser-rum";
import _ from "lodash";
import { brandsNotAllowedToOpenInNewTab } from "../controls/common/constants/specialCases";
import { locationTypeEnum } from "../utility/enum/locationTypeEnum";
import { PromotionEnumKey } from "../utility/enum/promotionEnumKey";
import { StayType } from "../utility/enum/stayTypeEnum";
import { useTranslation } from "../utility/function/useTranslations";


export const formattedDate = (date: any) => {
  const [year, month, day] = date?.split('-') ?? [];
  if (year == null || month == null || day == null) return date;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

export const getDestinationKey = (key: any) => {
  key = parseInt(key);
  switch (key) {
    case locationTypeEnum.country:
      return 'countryId';
    case locationTypeEnum.city:
      return 'cityId';
    case locationTypeEnum.property:
      return 'propertyId'
    default:
      return 'pincode'
  }
}

const WIDGET_OWNED_QUERY_PARAMS = new Set([
  'destid', 'desttype', 'checkindate', 'checkoutdate', 'staytype', 'guest',
  'propcode', 'promotype', 'promocode', 'corpcode', 'agencycode',
  'dlyx_xs', 'demand_calendar', 'locale', 'hc', 'nc',
  'chainid', 'brandid',
  'ac1', 'ac2', 'ac3', 'ac4', 'ac5',
  'cc1', 'cc2', 'cc3', 'cc4', 'cc5',
  'ca1', 'ca2', 'ca3', 'ca4', 'ca5',
]);

const getQueryStringFromUrl = (url: string): string => {
  if (!url) return '';
  const queryStart = url.indexOf('?');
  if (queryStart < 0) return '';
  let queryString = url.substring(queryStart + 1);
  const hashIndex = queryString.indexOf('#');
  if (hashIndex >= 0) {
    queryString = queryString.substring(0, hashIndex);
  }
  return queryString;
};

export const getUrlParamsToForward = (): string => {
  const paramMap = new Map<string, string>();

  const collectParams = (url: string, overwriteExisting = false) => {
    const queryString = getQueryStringFromUrl(url);
    if (!queryString) return;

    try {
      const urlSearchParams = new URLSearchParams(queryString);
      urlSearchParams.forEach((value, key) => {
        if (WIDGET_OWNED_QUERY_PARAMS.has(key.toLowerCase())) return;
        if (!overwriteExisting && paramMap.has(key)) return;
        paramMap.set(key, value);
      });
    } catch (_) { }
  };

  try {
    collectParams(window.location.href);
  } catch (_) { }

  try {
    if (window.parent && window.parent !== window) {
      collectParams(window.parent.location.href, true);
    }
  } catch (_) { }

  return Array.from(paramMap.entries())
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
};

export const createQueryString = (data: any) => {
  return Object.keys(data).filter(x => data[x] !== false).map(key => {
    let val = data[key]
    if (val === false) return ''
    if (val !== null && typeof val === 'object' && Object.keys(val).length > 0 && !Array.isArray(val)) val = createQueryString(val)
    if (key == 'chainID' || key == 'brandID') {
      return;
    }
    if (key == 'destination') {
      var newVal = val.split('&');
      //val = `${newVal[0]}&${newVal[2]}`;
      const destinationQuery = data[key];
      val = `destid=${destinationQuery?.id}&desttype=${destinationQuery?.type}`;
      return val;
    }
    if (key == 'checkInDate' || key == 'checkOutDate') {
      val = formattedDate(val);
    }
    if (key == 'stayType' && val != StayType.OVERNIGHT) {
      val = val; // Keep stayType as is
    }
    if (Array.isArray(val) && (key === 'guest')) {
      const arrayParams = new Array();
      val.forEach(obj => {
        switch (obj?.roomCount) {
          case 1: {
            if (obj.adultCount > 0)
              arrayParams.push('ac1=' + obj.adultCount);
            if (obj.childCount > 0) {
              arrayParams.push('cc1=' + obj.childCount);
              // Add child ages if available and not empty
              if (obj.childAges && Array.isArray(obj.childAges) && obj.childAges.length > 0) {
                const ages = obj.childAges.filter((age: number | null) => age !== null && age !== undefined);
                if (ages.length > 0) {
                  arrayParams.push('ca1=[' + ages.join(':') + ']');
                }
              }
            }
            break;
          }
          case 2: {
            if (obj.adultCount > 0)
              arrayParams.push('ac2=' + obj.adultCount);
            if (obj.childCount > 0) {
              arrayParams.push('cc2=' + obj.childCount);
              if (obj.childAges && Array.isArray(obj.childAges) && obj.childAges.length > 0) {
                const ages = obj.childAges.filter((age: number | null) => age !== null && age !== undefined);
                if (ages.length > 0) {
                  arrayParams.push('ca2=[' + ages.join(':') + ']');
                }
              }
            }
            break;
          }
          case 3: {
            if (obj.adultCount > 0)
              arrayParams.push('ac3=' + obj.adultCount);
            if (obj.childCount > 0) {
              arrayParams.push('cc3=' + obj.childCount);
              if (obj.childAges && Array.isArray(obj.childAges) && obj.childAges.length > 0) {
                const ages = obj.childAges.filter((age: number | null) => age !== null && age !== undefined);
                if (ages.length > 0) {
                  arrayParams.push('ca3=[' + ages.join(':') + ']');
                }
              }
            }
            break;
          }
          case 4: {
            if (obj.adultCount > 0)
              arrayParams.push('ac4=' + obj.adultCount);
            if (obj.childCount > 0) {
              arrayParams.push('cc4=' + obj.childCount);
              if (obj.childAges && Array.isArray(obj.childAges) && obj.childAges.length > 0) {
                const ages = obj.childAges.filter((age: number | null) => age !== null && age !== undefined);
                if (ages.length > 0) {
                  arrayParams.push('ca4=[' + ages.join(':') + ']');
                }
              }
            }
            break;
          }
          case 5: {
            if (obj.adultCount > 0)
              arrayParams.push('ac5=' + obj.adultCount);
            if (obj.childCount > 0) {
              arrayParams.push('cc5=' + obj.childCount);
              if (obj.childAges && Array.isArray(obj.childAges) && obj.childAges.length > 0) {
                const ages = obj.childAges.filter((age: number | null) => age !== null && age !== undefined);
                if (ages.length > 0) {
                  arrayParams.push('ca5=[' + ages.join(':') + ']');
                }
              }
            }
            break;
          }

          default:
            break;
        }
      });
      val = `${arrayParams}`;
      return `${key}=[${encodeURIComponent(`${val}`.replace(/\s/g, '_'))}]`
    }
    return `${key}=${encodeURIComponent(`${val}`.replace(/\s/g, '_'))}`
  }).filter(x => x).join('&')
}

export const bookNowHandler = (values: any, setToastObj: any, promotionData: any, destinationList: any, bookingURL: any, brandID: any, chainID: any, params: any, openinNewTab: boolean, dlysxParam: any, locale: string, nationalityCode?: string) => {
  const { t } = useTranslation();
  let propCode = '';
  if (Number(values.destination.type) === 2) {
    propCode = destinationList.find((x: { [x: string]: string; }) => x['propertyId'] === values.destination.id)?.code;
  }
  //console.log("bookNowHandler  propCode:", propCode)
  if (!values.destination?.id) {
    setToastObj({
      toastState: true,
      toastMessage: t("widget", "destinationIsRequired", locale)
    })
  }
  else {
    let catId = promotionData?.find((x: { key: PromotionEnumKey; }) => x.key === values?.promotion?.promotionType)?.promoCatId;
    const subCatId = promotionData?.find((x: { key: PromotionEnumKey; }) => x.key === PromotionEnumKey.contractedRate)?.subCategories.find((y: { key: any; }) => y.key === values?.promotion?.promotionType)?.promoScId
    if (subCatId) {
      catId = promotionData?.find((x: { key: PromotionEnumKey }) => x.key === PromotionEnumKey.contractedRate)?.promoCatId;
    }
    const requestCopy = _.cloneDeep(values);
    const request = { ...requestCopy }
    delete request.promotion;
    let key = getDestinationKey(values.destination.type);
    const isSingleProperty = destinationList.filter((x: { [x: string]: string; }) => x[key] === values.destination.id).length <= 1;
    const query = createQueryString(request);
    //console.logg("query:", {query})
    let ibeBookingUrl = bookingURL + '/reservation?' + query.replace('hC=', '');
    if (isSingleProperty && Number(values.destination.type) == 2) {
      ibeBookingUrl = bookingURL + '/reservation?' + query.replace('hC=', '') + '&propCode=' + propCode;
    }
    if (values?.promotion?.promotionType === "AAA") {
      ibeBookingUrl += '&PROMOTYPE=CAA_AAA';
    } else if (values?.promotion?.promotionType === "GOVERNMIL") {
      ibeBookingUrl += '&PROMOTYPE=GOVERNMIL'
    } else if (values?.promotion?.promotionType === "SENIORCITIZEN") {
      ibeBookingUrl += '&PROMOTYPE=SENIORCITIZEN';
    } else if (values?.promotion?.promotionType === "PROMOTIONALRATE") {
      ibeBookingUrl += '&PROMOCODE=' + values?.promotion?.promotionCode1;
    } else if (values?.promotion?.promotionType === "CORPORATERATE") {
      ibeBookingUrl += '&CORPCODE=' + values?.promotion?.promotionCode1 + "_" + values?.promotion?.promotionCode2;
    } else if (values?.promotion?.promotionType === "AGENCYRATE") {
      ibeBookingUrl += '&AGENCYCODE=' + values?.promotion?.promotionCode1 + "_" + values?.promotion?.promotionCode2;
    }

    if (nationalityCode !== undefined && nationalityCode !== null && `${nationalityCode}` !== '') {
      ibeBookingUrl += '&nc=' + encodeURIComponent(`${nationalityCode}`);
    }

    if (dlysxParam == -1) {
      dlysxParam = undefined
    } else if (!dlysxParam) {
      dlysxParam = getDlyxParam();
    }

    if (dlysxParam) {
      ibeBookingUrl += '&dlyx_xs=' + dlysxParam;
    }
    if (params.length > 1 && params[1] == "DEMAND_CALENDAR") {
      ibeBookingUrl += '&DEMAND_CALENDAR';
    }
    if (locale) {
      ibeBookingUrl += '&locale=' + locale;
    }
    const forwardedParams = getUrlParamsToForward();
    if (forwardedParams) {
      ibeBookingUrl += '&' + forwardedParams;
    }
    datadogRum.addAction('WidgetSearchButtonClicked', { ibeBookingUrl: ibeBookingUrl });
    //console.log({ibeBookingUrl})
    // ADDED FOR SAFARI BROWSER
    setTimeout(() => {
      if (openinNewTab && !brandsNotAllowedToOpenInNewTab(brandID)) {
        window.open(ibeBookingUrl.replace('&&&', '&'), '_blank');
      } else {
        //window.location.href = ibeBookingUrl.replace('&&&', '&');
        window.parent.location.href = ibeBookingUrl.replace('&&&', '&');
      }
    })

  }
}

export const setDlysxParamValue = (setDlysxParam: any) => {
  try {
    var _dlyxParam = (window as any)?.dlyx?.dlyx_xs;
    if (!_dlyxParam) {
      window.parent.postMessage('GET_DLYX', '*');
      if (window.parent) {

        const messageHandler = (event: MessageEvent) => {
          if (event?.data?.dlyx?.dlyx_xs) {
            _dlyxParam = event.data.dlyx.dlyx_xs;
            setDlysxParam(_dlyxParam)

            window.removeEventListener('message', messageHandler);
          }
        };

        window.addEventListener('message', messageHandler);
      }
    } else {
      setDlysxParam(_dlyxParam)
    }
  } catch (error) {
    //console.logg('Error in dlyx param:', error);
  }
}

const getDlyxParam = () => {
  try {
    var _dlyxParam = (window as any)?.dlyx?.dlyx_xs;
    if (!_dlyxParam) {
      window.parent.postMessage('GET_DLYX', '*');
      if (window.parent) {

        const messageHandler = (event: MessageEvent) => {
          if (event?.data?.dlyx?.dlyx_xs) {
            _dlyxParam = event.data.dlyx.dlyx_xs;
            window.removeEventListener('message', messageHandler);
          }
        };

        window.addEventListener('message', messageHandler);
      }
    }
  } catch (error) {
    //console.logg('Error in dlyx param:', error);
  }
  return _dlyxParam
}